import { useEffect, useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import { View } from 'smoothie';

import {
    type IncomeSplitOverviewHistory,
    type IncomeSplitProgress,
} from '../types/IncomeSplitOverviewTypes';
import { IncomeSplitProgressOverview } from './IncomeSplitProgressOverview';

const EMPTY_MONTH_PROGRESS: IncomeSplitProgress = {
    bills: { percentage: 0, cents: 0 },
    spend: { percentage: 0, cents: 0 },
    goals: { percentage: 0, cents: 0 },
};

export const IncomeSplitMonthCarousel = ({
    data,
    initialIndex = 0,
    monthKeysWithEmptyProgressCharts = new Set<string>(),
    setCurrentMonthIndex,
}: {
    data: IncomeSplitOverviewHistory[];
    initialIndex?: number;
    monthKeysWithEmptyProgressCharts?: Set<string>;
    setCurrentMonthIndex: (index: number) => void;
}) => {
    const [index, setIndex] = useState(initialIndex);

    const routes = Object.values(data).map((incomeSplitHistory) => ({
        key: incomeSplitHistory.month,
    }));

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: string } }) => {
        const monthData = data.find((month) => month.month === route.key);
        if (!monthData) return null;

        if (monthKeysWithEmptyProgressCharts.has(monthData.month)) {
            return (
                <IncomeSplitProgressOverview
                    progress={EMPTY_MONTH_PROGRESS}
                    thisExecutionProgress={EMPTY_MONTH_PROGRESS}
                />
            );
        }

        if (!monthData.progress) return null;
        return <IncomeSplitProgressOverview progress={monthData.progress} />;
    };

    const handleIndexChange = (newIndex: number) => {
        setIndex(newIndex);
        setCurrentMonthIndex(newIndex);
    };

    // sync index when initialIndex changes (from left/right buttons)
    useEffect(() => {
        if (initialIndex !== index) {
            setIndex(initialIndex);
        }
    }, [initialIndex, index]);

    // Don't render TabView if no routes
    if (routes.length === 0) {
        return null;
    }

    return (
        <View style={{ paddingBottom: 16, width: '100%' }}>
            <TabView
                swipeEnabled={true}
                navigationState={{ index, routes }}
                onIndexChange={handleIndexChange}
                renderScene={renderScene}
                renderTabBar={() => <></>}
                style={{ width: '100%', height: 130 }}
            />
        </View>
    );
};
